import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback } from 'react';

import { settings } from '@/lib/db';

import { type AppSettings, DEFAULT_SETTINGS, SETTINGS_KEYS } from './settings';

/**
 * Every setting, live, with defaults filled in — and a setter for each field.
 *
 * One `useLiveQuery` over the whole table rather than one per field: the
 * settings page reads all of them at once, and a field nobody has touched yet
 * simply isn't a row, which `DEFAULT_SETTINGS` covers rather than a schema
 * migration every time a new setting is added.
 *
 * Returns `null` while the database is still opening — same convention as
 * every other live query in this app (`tracks.count()` in `flights.index.tsx`,
 * and so on): `null` is "don't know yet", not "empty".
 */
export function useSettings(): AppSettings | null {
  const rows = useLiveQuery(() => settings.getAll(), [], null);
  if (rows === null) {
    return null;
  }
  return {
    altitudeUnit:
      (rows[SETTINGS_KEYS.altitudeUnit] as AppSettings['altitudeUnit'] | undefined) ??
      DEFAULT_SETTINGS.altitudeUnit,
    speedUnit:
      (rows[SETTINGS_KEYS.speedUnit] as AppSettings['speedUnit'] | undefined) ??
      DEFAULT_SETTINGS.speedUnit,
    distanceUnit:
      (rows[SETTINGS_KEYS.distanceUnit] as AppSettings['distanceUnit'] | undefined) ??
      DEFAULT_SETTINGS.distanceUnit,
    dateFormat:
      (rows[SETTINGS_KEYS.dateFormat] as AppSettings['dateFormat'] | undefined) ??
      DEFAULT_SETTINGS.dateFormat,
    timeZone: (rows[SETTINGS_KEYS.timeZone] as string | undefined) ?? DEFAULT_SETTINGS.timeZone,
    defaultPaletteId:
      (rows[SETTINGS_KEYS.defaultPaletteId] as string | undefined) ??
      DEFAULT_SETTINGS.defaultPaletteId,
  };
}

/** A single field, with a setter — for a component that only needs one and does not want the whole bundle. */
export function useSetting<K extends keyof AppSettings>(
  key: K,
): [AppSettings[K], (value: AppSettings[K]) => void] {
  const all = useSettings();
  const value = all?.[key] ?? DEFAULT_SETTINGS[key];
  const setValue = useCallback(
    (next: AppSettings[K]) => {
      void settings.set(SETTINGS_KEYS[key], next);
    },
    [key],
  );
  return [value, setValue];
}

export * from './settings';
